<script setup lang="ts">
/**
 * MY — Transfer of Shares preset panel (M32201). 3개 문서 패키지.
 *   1. Directors' Circular Resolution (Transfer) — 이사 전원(당사자 이사 이해관계 배제), 반복부 transfers
 *   2. Section 105 (Transfer)                    — 양도인/양수인 단건 평면 + 증인 4명 + 분류 체크박스
 *   3. Waiver of Preemption Rights (Transfer)    — 매도자 제외 기존 주주 전원, 반복부 transfers
 *
 * AGM / Allotment(M32200) 패턴 답습 — co-* 스타일, Person Lookup(✨), Drag&Drop, PreSetDateField,
 *   Governing Document 3-state, processSteps 기반 taskFlow, prepare-documents 저장.
 */
import { ref, computed, watch, onMounted, onUnmounted, nextTick, provide, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import { Icon } from '@iconify/vue';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail, OfficerRoleType } from '@/features/AI-documents/types/docGen.types';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import PreSetDateField from './PreSetDateField.vue';
import ContactRegisterModal from '@/features/startTask/preset_task/shared/preset/components/ContactRegisterModal.vue';
import {
  buildTransferSharesVariables,
  buildSection105Vars,
  securitiesDescriptionFromShareType,
  detectTransferSharesDocFlags,
  type GoverningDocumentType,
  type TransferRow,
  type Section105Data,
  type SignerInput,
} from '../mappers/transferSharesMapper';
import { resolveMaxCertNoFromSharesList } from '@/features/register/MY/utils/myRegisterPendingTransferUsage';
import { ringgitToWords } from '@/utils/ringgitToWords';
// ── 문서-뷰(Studio) 서브시스템 ──
import { TRANSFER_DOC_CTX } from './transfer/transferContext';
import { labelize as trfLabelize, typeOf as trfTypeOf, isDerivedKey, parseTransferKey } from './transfer/transferFields';
import TransferSharesPackageDoc from './transfer/TransferSharesPackageDoc.vue';
import type { SignerEntry } from '@/features/AI-documents/types/docGen.types';
import { fetchEntityAddresses } from '@/shared/composables/useEntityAddresses';

/** 정수(주식수) → 영문 단어 (Share Certificate shares_words). ringgitToWords 와 달리 접두/접미 없이 순수 숫자. */
const _WONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const _WTENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const _WSCALES = ['', 'Thousand', 'Million', 'Billion', 'Trillion'];
function _three(n: number): string {
  const out: string[] = [];
  const h = Math.floor(n / 100), r = n % 100;
  if (h > 0) out.push(_WONES[h], 'Hundred');
  if (r > 0) out.push(r < 20 ? _WONES[r] : (_WTENS[Math.floor(r / 10)] + (r % 10 ? ' ' + _WONES[r % 10] : '')));
  return out.join(' ').trim();
}
function sharesToWords(v: any): string {
  let n = Math.floor(Number(String(v ?? '').replace(/[^0-9]/g, '')) || 0);
  if (n <= 0) return '';
  const groups: number[] = [];
  while (n > 0) { groups.push(n % 1000); n = Math.floor(n / 1000); }
  const parts: string[] = [];
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i] === 0) continue;
    parts.push(_three(groups[i]) + (_WSCALES[i] ? ' ' + _WSCALES[i] : ''));
  }
  return parts.join(' ').trim();
}

/** Transfer 행 1건에 딸린 Section 105 기본 데이터 (증인 4명 포함). */
function emptySection105(): Section105Data {
  return {
    considerationWords: '',
    considerationFigures: '',
    transferorDetails: '',
    transferorId: '',
    transfereeDetails: '',
    transfereePerson: '',
    transfereeCorporate: '',
    transfereeKind: 'individual',
    transfereeRace: '',
    transfereeCorporateType: '',
    witnesses: [
      { name: '', address: '', occupation: '' },
      { name: '', address: '', occupation: '' },
      { name: '', address: '', occupation: '' },
      { name: '', address: '', occupation: '' },
    ],
  };
}

/** 빈 Transfer 행 (Section 105 포함). */
function emptyTransferRow(): TransferRow {
  return {
    transferorName: '', transferorContactCd: '', certificateNo: '', shares: '', shareType: 'Ordinary',
    transfereeName: '', transfereeContactCd: '', newCertificateNo: '', newShares: '',
    section105: emptySection105(),
  };
}

interface PersonLookupContext {
  fieldGroup:
    | 'director'
    | 'waiverMember'
    | 'rowTransferor'
    | 'rowTransferee'
    | 'witness'
    | 'constitution-clause';
  index?: number;
  /** witness 전용 — 해당 transfer 행 내 증인 인덱스(0~3). */
  subIndex?: number;
  defaultRole?: OfficerRoleType | null;
  /** constitution-clause 전용 — AI 챗봇 clause 위젯 옵션/기본값. */
  key?: string;
  clauseOptions?: string[];
  defaultBasis?: string;
  defaultValue?: string;
}

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: PersonLookupContext | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  /** 문서-뷰(Studio) 셸에서 렌더될 때 true — 자체 헤더/푸터 숨김, Generate 는 셸이 호출. */
  studio?: boolean;
  /** studio 챗봇 정관 Clause 선택 결과 (PreSetDocGenView.studioAppliedClause). */
  appliedClause?: { clauseNumber: string; governingBasis: 'constitution' | 'articles' | 'third_schedule' | null; rawOption?: string; seq: number } | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'create-task', data: { taskTitle: string }): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', context: PersonLookupContext): void;
  (e: 'request-address-lookup', context: { fieldGroup: string; index: number; addresses: any[]; subjectLabel?: string }): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
}>();

const router = useRouter();
const editMode = computed(() => !!props.editTaskId);

// ─── Entity ───────────────────────────────────────────────────────────────
const entityCd = computed(() => String(props.entityData?.entityCd || props.entityData?.icpCd || '').trim());
const companyName = ref('');
const companyRegistrationNo = ref('');
const companyRegistrationNoOld = ref('');
const articleNo = ref('');
const constitutionClauseNo = ref('');
const entityLoading = ref(false);
const articleNoOptions = ref<string[]>([]);
const entityClauseRegistered = ref(false);

// ─── Governing Document (Resolution Basis) — 3-state ───────────────────────
const governingDocumentType = ref<GoverningDocumentType>('constitution');
watch(governingDocumentType, (t) => { if (t === 'third_schedule') constitutionClauseNo.value = ''; });

/** 등록된 clause 옵션 문자열이 어떤 Governing Document 종류인지 분류. */
function clauseOptionType(opt: string): 'constitution' | 'articles' | 'third_schedule' | 'other' {
  const s = String(opt || '');
  // 우선순위: Third Schedule > Articles(Article(s)·구 Table A·Regulation·1965·LLP) > Constitution(Clause).
  //   setter(clauseOptionType 위임)와 동일 분류 → 탭 튕김 없음.
  if (/third\s+schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s) || /\btable\s+a\b/i.test(s) || /\bllp\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return 'other';
}
/** 선택된 Governing Document 라디오에 맞는 clause 옵션만 노출 (분류 불가 'other' 는 유지). */
const filteredClauseOptions = computed<string[]>(() =>
  articleNoOptions.value.filter((o) => {
    const ot = clauseOptionType(o);
    return ot === governingDocumentType.value || ot === 'other';
  })
);
// 잠금은 오직 Entity 등록 여부로만 결정한다 (new/edit 무관, 미등록이면 편집 가능).
const governingDocLocked = computed(() => entityClauseRegistered.value);
function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}
const constitutionClauseModel = computed<string>({
  get: () => constitutionClauseNo.value,
  set: (val: string | null) => {
    const s = val || '';
    constitutionClauseNo.value = parseClauseNo(s);
    // 필터(clauseOptionType)와 동일 분류기로 라디오 자동 감지 — 탭 튕김 방지.
    const detected = clauseOptionType(s);
    if (detected !== 'other') governingDocumentType.value = detected;
  },
});

// ─── 구성 분기 ─────────────────────────────────────────────────────────────
const directorCount = ref<'single' | 'multiple'>('multiple');
const memberCount = ref<'single' | 'multiple'>('multiple');
const hasCommonSeal = ref(false);

// ─── Entity 주식(증서) — transferor 선택 시 Existing Cert No. 자동 채움 ─────
type EntityShareRow = {
  contactCd?: string;
  certificateNo?: string;
  numberOfShares?: number;
  shareType?: string;
};
const entityShareRows = ref<EntityShareRow[]>([]);

type ShareCertOption = { title: string; value: string; shares: number };

function getShareCertsForContact(contactCd: string): Array<{ certificateNo: string; shares: number; shareType: string }> {
  const cc = String(contactCd || '').trim();
  if (!cc) return [];
  return entityShareRows.value
    .filter((r) => String(r.contactCd || '').trim() === cc)
    .filter((r) => (Number(r.numberOfShares ?? 0) || 0) > 0 || String(r.certificateNo || '').trim())
    .map((r) => ({
      certificateNo: String(r.certificateNo || '').trim(),
      shares: Number(r.numberOfShares ?? 0) || 0,
      shareType: String(r.shareType || '').trim() || 'Ordinary',
    }));
}

function buildCertSelectOptions(contactCd: string): ShareCertOption[] {
  // 표시 형식만 변경 (value=certificateNo 는 그대로) — "Cert No. {번호} ({주식수} shares)".
  //   item-title 이므로 드롭다운 항목과 선택된 값 표시가 동일 형식으로 나온다. 순서는 map 이 원본 유지.
  return getShareCertsForContact(contactCd).map((c) => ({
    title: c.certificateNo
      ? `Cert No. ${c.certificateNo} (${c.shares.toLocaleString()} shares)`
      : `${c.shares.toLocaleString()} shares`,
    value: c.certificateNo,
    shares: c.shares,
  }));
}

function applyTransferorCertsToRow(rowIndex: number, contactCd: string) {
  const row = transfers.value[rowIndex];
  if (!row) return;
  row.transferorContactCd = String(contactCd || '').trim();
  const certs = getShareCertsForContact(row.transferorContactCd);
  if (certs.length >= 1) {
    // 단일이면 그 증서, 복수면 첫 증서를 기본값으로 자동 세팅(문서에서 편집 가능).
    row.certificateNo = certs[0].certificateNo;
    row.shares = String(certs[0].shares);
    row.shareType = certs[0].shareType;
  } else {
    row.certificateNo = '';
    row.shares = '';
  }
  // New No. of Shares 는 기본적으로 양도 주식수와 동일(비어있을 때만; 부분 재발행 시 편집).
  if (!String(row.newShares || '').trim() && String(row.shares || '').trim()) row.newShares = String(row.shares);
  // 양도인/증서가 정해지면 New Cert No = 기존 최대 cert + 1 자동(비어있을 때만).
  applyNewCertNoToRow(rowIndex);
}

function transferorCertOptions(rowIndex: number): ShareCertOption[] {
  const cc = String(transfers.value[rowIndex]?.transferorContactCd || '').trim();
  return buildCertSelectOptions(cc);
}

function onTransferorCertSelect(rowIndex: number, certNo: string | null) {
  const row = transfers.value[rowIndex];
  if (!row) return;
  const hit = getShareCertsForContact(String(row.transferorContactCd || '').trim()).find((c) => c.certificateNo === certNo);
  if (!hit) return;
  row.certificateNo = hit.certificateNo;
  row.shares = String(hit.shares);
  row.shareType = hit.shareType;
}

// ─── Section 105 auto-매핑 표시 (Transfer Information → readonly) ───────────
function s105NumberOfUnits(i: number): string { return String(transfers.value[i]?.shares ?? ''); }
function s105CertificateNo(i: number): string { return String(transfers.value[i]?.certificateNo ?? ''); }
function s105Securities(i: number): string { return securitiesDescriptionFromShareType(transfers.value[i]?.shareType); }

/** 엔티티 멤버 증서 + 동일 폼 내 이미 배정된 New Cert No. 기준 다음 번호. */
function resolveNextNewCertificateNo(excludeRowIndex?: number): string {
  const rows: Record<string, unknown>[] = entityShareRows.value.map((r) => ({
    CERTI_NO: r.certificateNo,
  }));
  transfers.value.forEach((t, i) => {
    if (i === excludeRowIndex) return;
    const nc = String(t.newCertificateNo || '').trim();
    if (nc) rows.push({ CERTI_NO: nc });
  });
  const max = resolveMaxCertNoFromSharesList(rows);
  return String(max + 1);
}

function applyNewCertNoToRow(rowIndex: number) {
  const row = transfers.value[rowIndex];
  if (!row) return;
  if (!String(row.newCertificateNo || '').trim()) {
    row.newCertificateNo = resolveNextNewCertificateNo(rowIndex);
  }
}

// ─── 양도 내역 (반복부: DCR·Waiver + 행별 Section 105) ─────────────────────
type TransferRowState = TransferRow;
const transfers = ref<TransferRowState[]>([emptyTransferRow()]);
function addTransfer() {
  transfers.value.push(emptyTransferRow());
}
function removeTransfer(i: number) {
  transfers.value.splice(i, 1);
  if (transfers.value.length === 0) addTransfer();
}
// Total Shares = New No. of Shares(양수인에게 재발행되는 신규 주식수) 합계.
const totalSharesTransferred = computed(() =>
  transfers.value.reduce((sum, t) => sum + (Number(t.newShares || 0) || 0), 0),
);

// ─── 날짜 (execution 통합 + 문서별 override + X skip) ──────────────────────
const executionDate = ref('');
const resolutionDate = ref(''); // DCR override (blank → execution 따라감)
const transferDate = ref('');   // Section 105 override
const letterDate = ref('');     // Waiver override
const skippedFields = ref<Set<string>>(new Set());
function isFieldSkipped(field: string): boolean { return skippedFields.value.has(field); }
function toggleFieldSkip(field: string, skip: boolean) {
  if (skip) skippedFields.value.add(field);
  else skippedFields.value.delete(field);
}
const executionEffective = computed(() => (isFieldSkipped('executionDate') ? '' : executionDate.value));
// 각 문서 날짜 — skip 이면 '', override 있으면 그 값, 아니면 execution 따라감.
const resolutionEffective = computed(() =>
  isFieldSkipped('resolutionDate') ? '' : (resolutionDate.value || executionEffective.value),
);
const transferEffective = computed(() =>
  isFieldSkipped('transferDate') ? '' : (transferDate.value || executionEffective.value),
);
const letterEffective = computed(() =>
  isFieldSkipped('letterDate') ? '' : (letterDate.value || executionEffective.value),
);

// ─── 서명 주체 (문서1 이사 / 문서3 주주) ──────────────────────────────────
const directors = ref<SignerInput[]>([]);
const waiverMembers = ref<SignerInput[]>([]);        // Waiver 서명자(사용자 확정) — 기본 비움, 자동입력 버튼으로 채움
const entityMembers = ref<SignerInput[]>([]);        // 기존 주주 풀(자동입력 소스). 서명자로 자동 노출하지 않음.
function addDirector() { directors.value.push({ contactCd: '', name: '', email: '', nricNo: '' }); }
function removeDirector(i: number) { directors.value.splice(i, 1); }
function addWaiverMember() { waiverMembers.value.push({ contactCd: '', name: '', email: '', nricNo: '' }); }
function removeWaiverMember(i: number) { waiverMembers.value.splice(i, 1); }

// ── Share Certificate (Stage 4) — 발행 관련 필드/서명자 ──
const registeredOfficeAddress = ref('');
const issueDate = ref(''); // 미입력 시 issueEffective 가 결의/양도/실행일 따라감
const shareCertSigners = ref<SignerInput[]>([]);
const issueEffective = computed(() => issueDate.value || resolutionEffective.value || transferEffective.value || executionEffective.value);

// ─── 문서 분기 플래그 (templateName 자동 감지) ────────────────────────────
const docFlags = computed(() => detectTransferSharesDocFlags(props.selection?.documents || []));

// ─── 진행/검증 ─────────────────────────────────────────────────────────────
const creating = ref(false);
const progress = ref('');

// ─── Edit dirty 판정 (Update Task 활성/비활성) ──────────────────────────────
// 편집 진입 시 복원 + entity 로딩이 settle 된 상태를 기준 스냅샷으로 잡고,
//   이후 사용자가 값을 바꾸면(현재 state ≠ 기준) Update Task 를 활성화한다.
const restored = ref(false);
const baselineSnapshot = ref<string | null>(null);
function captureBaseline() {
  baselineSnapshot.value = JSON.stringify(gatherState());
}
const isDirty = computed(() => {
  if (baselineSnapshot.value == null) return false;
  return JSON.stringify(gatherState()) !== baselineSnapshot.value;
});
// entity 비동기 로딩이 restore 이후 값을 덮어쓸 수 있으므로, 로딩 종료 시 기준을 재확정.
watch(entityLoading, (loading, prev) => {
  if (prev && !loading && editMode.value && restored.value) {
    nextTick(() => captureBaseline());
  }
});
// 진행 상태는 호스트(PreSetDocGenView)의 공통 중앙 오버레이로 흘려보낸다. (AOD 동일 패턴)
const presetProgress = usePresetProgress();
watch(creating, (v) => { presetProgress.busy = v; });
watch(progress, (v) => { presetProgress.message = v; });
const taskConfigSteps = ref<string[]>([]);
const isFormValid = computed(() => {
  if (!entityCd.value) return false;
  // 최소 1건의 양도(양도인+양수인).
  return transfers.value.some((t) => String(t.transferorName || '').trim() && String(t.transfereeName || '').trim());
});

// ─── Drag & Drop ───────────────────────────────────────────────────────────
const isDragging = ref(false);
function _onGlobalDragStart() { isDragging.value = true; }
function _onGlobalDragEnd() { isDragging.value = false; }
function handleFieldDrop(event: DragEvent, setter: (v: string) => void) {
  isDragging.value = false;
  const json = event.dataTransfer?.getData('application/x-entity-field');
  if (json) {
    try { const { value } = JSON.parse(json); if (value) { setter(String(value)); return; } } catch { /* ignore */ }
  }
  const text = event.dataTransfer?.getData('text/plain');
  if (text) setter(String(text));
}

// ─── Person Lookup (✨) ────────────────────────────────────────────────────
function requestLookup(ctx: PersonLookupContext) {
  emit('request-person-lookup', ctx);
}

function applyPersonToField(ctx: PersonLookupContext, person: any) {
  if (!person) return;
  const name = person.contactName || person.name || person.CONTACT_NAME || '';
  const idNo = person.contactRegNo || person.nricNo || person.CONTACT_REG_NO || '';
  const addr = person.primaryAddress || person.addresses?.[0]?.value || '';
  const email = person.contactEmail || person.CONTACT_EMAIL || '';
  const nationality = person.nationalityName || person.nationality || '';
  const contactCd = String(person.contactCd || person.CONTACT_CD || '').trim();

  switch (ctx.fieldGroup) {
    case 'director': {
      const i = ctx.index ?? -1;
      if (i >= 0 && directors.value[i]) {
        directors.value[i].name = name;
        directors.value[i].contactCd = contactCd;
        directors.value[i].email = email;
        directors.value[i].nricNo = idNo;
      }
      break;
    }
    case 'waiverMember': {
      const i = ctx.index ?? -1;
      if (i >= 0 && waiverMembers.value[i]) {
        waiverMembers.value[i].name = name;
        waiverMembers.value[i].contactCd = contactCd;
        waiverMembers.value[i].email = email;
        waiverMembers.value[i].nricNo = idNo;
      }
      break;
    }
    case 'rowTransferor': {
      const i = ctx.index ?? -1;
      const row = transfers.value[i];
      if (i >= 0 && row) {
        row.transferorName = name;
        row.transferorRegNo = idNo; // 레지스터 TFOR_REG_NO 용
        applyTransferorCertsToRow(i, contactCd);
        // Section 105 양도인 보조 필드 자동 채움 (수정 가능)
        const s = ensureRowSection105(i);
        s.transferorDetails = name;
        s.transferorId = idNo;
      }
      break;
    }
    case 'rowTransferee': {
      const i = ctx.index ?? -1;
      const row = transfers.value[i];
      if (i >= 0 && row) {
        row.transfereeName = name;
        row.transfereeContactCd = contactCd;
        row.transfereeRegNo = idNo; // 레지스터 TEE_REG_NO 용
        applyNewCertNoToRow(i);
        // Section 105 양수인 보조 필드 자동 채움 (수정 가능)
        const s = ensureRowSection105(i);
        s.transfereeDetails = [name, addr].filter(Boolean).join('\n');
        // 양수인 법인/개인(Individual/Corporate) 자동 선택 —
        //   1순위: 연락처 CATEGORY_CD (B09001=법인, B09002=개인) — getContactDetail 이 person.categoryCd 로 반환.
        //   2순위(폴백): 회사명 접미사 휴리스틱(SDN BHD / PTE LTD / BERHAD / LLP / LIMITED 등).
        const catCd = String(
          (person as any).categoryCd || (person as any).CATEGORY_CD || (person as any).category?.code || '',
        ).trim().toUpperCase();
        const looksCorporate = /(\bSDN\.?\s*BHD\b|\bBERHAD\b|\bBHD\b|\bPTE\.?\s*LTD\b|\bLTD\b|\bLIMITED\b|\bLLP\b|\bPLT\b|\bINC\b|\bCORP(?:ORATION)?\b)/i.test(name);
        if (catCd === 'B09001') s.transfereeKind = 'corporate';
        else if (catCd === 'B09002') s.transfereeKind = 'individual';
        else if (looksCorporate) s.transfereeKind = 'corporate';
        // corporate/person 보조필드는 (자동 갱신된) transfereeKind 기준으로 채운다.
        if (s.transfereeKind === 'corporate') {
          s.transfereeCorporate = idNo;
          s.transfereeRace = ''; // 법인 양수인 → Race(개인 전용) 값 제거
        } else {
          s.transfereePerson = [idNo, nationality].filter(Boolean).join(', ');
          // 양수인 Race 자동 선택 — 선택된 사람의 race("Malay"/"Chinese"/...) → 라디오 값 매핑.
          s.transfereeRace = mapRaceToTransfereeRace((person as any).race);
        }
      }
      break;
    }
    case 'witness': {
      const i = ctx.index ?? -1;
      const wi = ctx.subIndex ?? -1;
      if (i >= 0 && wi >= 0) {
        const s = ensureRowSection105(i);
        const w = s.witnesses?.[wi];
        if (w) {
          w.name = name;
          if (addr) w.address = addr;
          const occ = person.occupation || person.OCCUPATION || person.designation || '';
          if (occ) w.occupation = occ;
        }
      }
      break;
    }
  }
}

/** 행의 section105 를 보장(없으면 초기화)하고 반환. */
function ensureRowSection105(i: number): Section105Data {
  const row = transfers.value[i];
  if (!row.section105) row.section105 = emptySection105();
  return row.section105;
}

/** 선택된 사람의 race 문자열("MALAY"/"Chinese"/...) → 양수인 Race 라디오 값. 매칭 없으면 others, 빈값이면 ''. */
function mapRaceToTransfereeRace(raceRaw: unknown): Section105Data['transfereeRace'] {
  const r = String(raceRaw ?? '').trim().toLowerCase();
  if (!r) return '';
  if (r.includes('malay')) return 'malay';
  if (r.includes('chinese')) return 'chinese';
  if (r.includes('indian')) return 'indian';
  return 'others';
}

/** Individual/Corporate 라디오 변경 — 법인 선택 시 Race(개인 전용) 값 제거. */
function onTransfereeKindChange(i: number, value: unknown): void {
  const s = transfers.value[i]?.section105;
  if (!s) return;
  s.transfereeKind = value === 'corporate' ? 'corporate' : 'individual';
  if (s.transfereeKind === 'corporate') {
    s.transfereeRace = '';
  }
}

// ── Consideration 자동 단어변환 (RM figures → in words) ──────────────────────
//   "Consideration (RM, figures)" 입력 시 "Consideration (in words)" 를 Ringgit Malaysia 표기로 자동 생성.
//   사용자가 words 를 직접 수정하면 자동생성 중단, 비우면 재개. (행 단위 — section105 객체 식별로 추적)
const considerationWordsManual = new WeakSet<object>();

/** 문자열이 동일 단위의 반복(X, XX, XXX...)이면 단위 1개로 축약. "AA"→"A". 아니면 원본. */
function collapseSelfRepeat(str: string): string {
  const s = String(str || '').trim();
  const n = s.length;
  if (n < 2) return s;
  for (let len = 1; len <= n / 2; len++) {
    if (n % len !== 0) continue;
    const unit = s.slice(0, len);
    if (unit.repeat(n / len) === s) return unit;
  }
  return s;
}

/** figures 입력 → 수동 오버라이드가 아니면 words 자동 생성 (빈/유효하지 않으면 words clear). */
function onConsiderationFiguresInput(i: number, value: unknown): void {
  const s = transfers.value[i]?.section105;
  if (!s) return;
  s.considerationFigures = value == null ? '' : String(value);
  if (!considerationWordsManual.has(s)) {
    s.considerationWords = ringgitToWords(s.considerationFigures);
  }
}

/** words 직접 입력 → 수동 오버라이드로 표시(자동생성 중단). 비우면 오버라이드 해제 + figures 로 재생성. */
function onConsiderationWordsInput(i: number, value: unknown): void {
  const s = transfers.value[i]?.section105;
  if (!s) return;
  const val = value == null ? '' : String(value);
  if (val.trim() === '') {
    considerationWordsManual.delete(s);
    s.considerationWords = ringgitToWords(s.considerationFigures);
  } else {
    considerationWordsManual.add(s);
    s.considerationWords = val;
  }
}

/**
 * 복원/마운트 시 — 저장된 words 정합성 보정.
 *   ⚠️ 과거 버그로 words 가 auto 값의 자기중복(예 "…Only…Only")으로 저장된 케이스가 있어,
 *      auto 값(또는 그 반복)과 일치하면 수동으로 오판하지 않고 single auto 로 정규화 → 자동생성 재개.
 *   그 외(진짜 사용자 수정) 만 수동 오버라이드로 표시(덮어쓰기 방지).
 */
function initConsiderationManualFlags(): void {
  for (const t of transfers.value) {
    const s = t.section105;
    if (!s) continue;
    const w = String(s.considerationWords || '').trim();
    if (!w) continue;
    const auto = ringgitToWords(s.considerationFigures);
    if (auto && collapseSelfRepeat(w) === auto) {
      // auto 값 또는 그 중복 → 자동생성으로 간주 + 중복 제거(정규화). 수동 마킹 안 함.
      if (w !== auto) s.considerationWords = auto;
    } else if (w !== auto) {
      considerationWordsManual.add(s);
    }
  }
}

watch(
  () => props.selectedPerson,
  (person) => {
    if (!person) return;
    const ctx = props.activePersonContext;
    if (!ctx) return;
    applyPersonToField(ctx, person);
  },
);

// ── 공통 Contact 등록 모달 (AOD / Secretary 패턴) ──
const contactRegisterOpen = ref(false);
let contactRegisterTarget: PersonLookupContext | null = null;

function openContactRegister(ctx: PersonLookupContext) {
  contactRegisterTarget = ctx;
  contactRegisterOpen.value = true;
}

async function onContactRegistered(payload: { contactCd: string; type?: 'individual' | 'corporate'; data: any }) {
  const ctx = contactRegisterTarget;
  contactRegisterTarget = null;
  if (!ctx || !payload?.contactCd) return;

  let person: any = { contactCd: payload.contactCd };
  try {
    const res: any = await docGenApi.getContactDetail(entityCd.value || '', payload.contactCd);
    const detail = res?.data || res;
    if (detail) {
      person = {
        ...detail,
        contactCd: payload.contactCd,
        contactName: detail.contactName || detail.name || payload.data?.fullName || payload.data?.companyName || '',
        addresses: Array.isArray(detail.addresses) ? detail.addresses : [],
      };
    } else if (payload.data) {
      const d = payload.data;
      person = {
        contactCd: payload.contactCd,
        contactName: d.fullName || d.companyName || [d.firstName, d.middleName, d.surname].filter(Boolean).join(' '),
        contactRegNo: d.identificationNo || d.registrationNo || d.uen || '',
        contactEmail: d.email || '',
        nationalityName: d.nationality || '',
      };
    }
  } catch (err) {
    console.warn('[TransferShares] getContactDetail after register failed:', err);
  }
  applyPersonToField(ctx, person);
}

// ─── Entity 자동 채움 ──────────────────────────────────────────────────────
async function loadEntity(cd: string) {
  if (!cd) return;
  entityLoading.value = true;
  try {
    // 회사 기본정보 + 정관(article/clause)
    try {
      const res = await fetch(`/api/setupEntity/changeAddress/address-info/${cd}`, { credentials: 'include' });
      const json = await res.json();
      if (json?.success || json?.data) {
        const d = json.data || json;
        companyName.value = d.companyName || d.entityName || companyName.value;
        companyRegistrationNo.value = d.regNo || d.registrationNo || d.companyRegistrationNo || companyRegistrationNo.value;
        companyRegistrationNoOld.value = d.regNoOld || d.registrationNoOld || companyRegistrationNoOld.value;
        articleNo.value = d.articleNo || '';
        if (!constitutionClauseNo.value && d.clauseNo) constitutionClauseNo.value = String(d.clauseNo);
        if (articleNo.value) {
          entityClauseRegistered.value = true;
          applyGoverningFromText(articleNo.value);
        }
      }
    } catch (err) { console.warn('[TransferShares] entity info fetch failed:', err); }

    // Governing Document 옵션 (articleNoOptions)
    try {
      const res = await fetch(`/api/entity_my/profile/resolution-options`, { credentials: 'include' });
      const json = await res.json();
      const opts = json?.articleNoOptions || json?.data?.resolution?.articleNoOptions || json?.resolution?.articleNoOptions || [];
      articleNoOptions.value = Array.isArray(opts) ? opts.map((o: any) => String(o)) : [];
    } catch (err) { console.warn('[TransferShares] resolution-options fetch failed:', err); articleNoOptions.value = []; }

    // Registered Office (Share Certificate) — 비어있으면 entity 등록주소로 자동.
    //   ⚠ 비차단(fire-and-forget): 이 fetch 가 느리거나 hang 돼도 아래 이사/주주/**주식(증서) 로드**를 막지 않도록 await 하지 않는다.
    //     (cert No·주식수 자동채움은 entity_my/shares 로드에 의존하므로 이 블록이 앞에서 blocking 되면 자동채움이 멈춘다.)
    fetchEntityAddresses(cd)
      .then((gov) => { if (gov?.registered && !registeredOfficeAddress.value) registeredOfficeAddress.value = gov.registered; })
      .catch((err) => console.warn('[TransferShares] registered office fetch failed:', err));

    // 이사 전원 → directors prefill (DCR). 당사자 이사는 사용자가 × 로 제외.
    try {
      const dirRes: any = await docGenApi.getDirectors(cd);
      const dirs = dirRes?.data?.directors || dirRes?.data || [];
      if (Array.isArray(dirs) && dirs.length > 0 && directors.value.length === 0) {
        directors.value = dirs.map((d: any): SignerInput => ({
          contactCd: d.contactCd || d.contactCode || '',
          name: d.name || d.contactName || '',
          email: d.contactEmail || '',
          nricNo: d.contactRegNo || '',
        }));
        directorCount.value = directors.value.length <= 1 ? 'single' : 'multiple';
      }
    } catch (err) { console.warn('[TransferShares] getDirectors failed:', err); }

    // 기존 주주 전원 → entityMembers 풀(자동입력 소스). Waiver 서명란은 기본 비움 — 사용자가 "자동 입력" 클릭 시 채움.
    try {
      const memRes: any = await docGenApi.getMembers(cd);
      const mems = memRes?.data?.members || memRes?.data?.shares || memRes?.data || [];
      if (Array.isArray(mems) && mems.length > 0) {
        const seen = new Set<string>();
        const dedup: any[] = [];
        for (const m of mems) {
          const cc = String(m.contactCd || m.contactCode || '').trim();
          const key = cc || `name:${String(m.name || m.contactName || '').toUpperCase()}`;
          if (seen.has(key)) continue;
          seen.add(key);
          dedup.push(m);
        }
        entityMembers.value = dedup.map((m: any): SignerInput => ({
          contactCd: m.contactCd || m.contactCode || '',
          name: m.name || m.contactName || '',
          email: m.contactEmail || '',
          nricNo: m.contactRegNo || '',
        }));
        memberCount.value = entityMembers.value.length <= 1 ? 'single' : 'multiple';
      }
    } catch (err) { console.warn('[TransferShares] getMembers failed:', err); }

    // Entity Shares — transferor 선택 시 증서 번호·주식수 콤보 (AllotmentShares 와 동일 raw fetch).
    try {
      const shRes = await fetch(`/api/entity_my/shares/${encodeURIComponent(cd)}`, { credentials: 'include' });
      if (!shRes.ok) {
        console.warn('[TransferShares] entity_my/shares not ok — cert auto-load skipped:', shRes.status);
        entityShareRows.value = [];
      } else {
        const shJson: any = await shRes.json();
        const shData = shJson?.data?.shares ? shJson.data : (shJson?.data?.data ?? shJson?.data ?? shJson ?? {});
        const shares = Array.isArray(shData?.shares) ? shData.shares : [];
        entityShareRows.value = shares.map((s: any) => ({
          contactCd: String(s.contactCd || '').trim(),
          certificateNo: String(s.certificateNo || s.certiNo || '').trim(),
          numberOfShares: Number(s.numberOfShares ?? s.acqNumberOfShares ?? 0) || 0,
          shareType: s.shareType || '',
        }));
      }
    } catch (err) {
      console.warn('[TransferShares] entity_my/shares failed:', err);
      entityShareRows.value = [];
    }
  } finally {
    entityLoading.value = false;
  }
}

/** Entity 등록 Governing Document 텍스트 → 라디오 타입 + clause 번호. */
function applyGoverningFromText(text: string) {
  const s = String(text || '');
  if (/third\s+schedule/i.test(s)) { governingDocumentType.value = 'third_schedule'; constitutionClauseNo.value = ''; return; }
  if (/articles?\s+of\s+association/i.test(s)) governingDocumentType.value = 'articles';
  else governingDocumentType.value = 'constitution';
  constitutionClauseNo.value = parseClauseNo(s);
}

watch(entityCd, (cd) => { if (cd) loadEntity(cd); }, { immediate: true });

watch(
  () => props.entityData,
  (ed) => {
    if (!ed) return;
    if (!companyName.value && (ed.companyName || ed.entityName)) {
      companyName.value = String(ed.companyName || ed.entityName || '').trim();
    }
    if (!companyRegistrationNo.value && (ed.registrationNo || ed.regNo)) {
      companyRegistrationNo.value = String(ed.registrationNo || ed.regNo || '').trim();
    }
    if (!companyRegistrationNoOld.value && (ed.registrationNoOld || ed.regNoOld)) {
      companyRegistrationNoOld.value = String(ed.registrationNoOld || ed.regNoOld || '').trim();
    }
  },
  { immediate: true },
);

// ─── 문서 라벨 / skip 표시 ────────────────────────────────────────────────
function docLabel(d: any): string {
  return String(d?.templateName || d?.docCode || 'Document').replace(/\.docx?$/i, '');
}
function isSection105Doc(d: any): boolean {
  return isSection105Template(String(d?.templateName || d?.docCode || ''));
}
/** Section 105 예상 생성 부수 = 유효 transfer 수 (최소 1). */
const plannedSection105Count = computed(() => Math.max(1, validTransfers().length));

// ─── gather / restore (Edit) ───────────────────────────────────────────────
function gatherState(): Record<string, any> {
  return {
    companyName: companyName.value,
    companyRegistrationNo: companyRegistrationNo.value,
    companyRegistrationNoOld: companyRegistrationNoOld.value,
    constitutionClauseNo: constitutionClauseNo.value,
    governingDocumentType: governingDocumentType.value,
    directorCount: directorCount.value,
    memberCount: memberCount.value,
    hasCommonSeal: hasCommonSeal.value,
    // 각 transfer 행이 자체 Section 105 (section105) 를 포함.
    transfers: transfers.value,
    executionDate: executionDate.value,
    resolutionDate: resolutionDate.value,
    transferDate: transferDate.value,
    letterDate: letterDate.value,
    skippedFields: Array.from(skippedFields.value),
    directors: directors.value,
    waiverMembers: waiverMembers.value,
    // Share Certificate (Stage 4)
    registeredOfficeAddress: registeredOfficeAddress.value,
    issueDate: issueDate.value,
    shareCertSigners: shareCertSigners.value,
  };
}
function restoreState(s: Record<string, any>) {
  if (!s || typeof s !== 'object') return;
  companyName.value = s.companyName || companyName.value;
  companyRegistrationNo.value = s.companyRegistrationNo || companyRegistrationNo.value;
  companyRegistrationNoOld.value = s.companyRegistrationNoOld || companyRegistrationNoOld.value;
  constitutionClauseNo.value = s.constitutionClauseNo || '';
  if (s.governingDocumentType) governingDocumentType.value = s.governingDocumentType;
  if (s.directorCount) directorCount.value = s.directorCount;
  if (s.memberCount) memberCount.value = s.memberCount;
  hasCommonSeal.value = !!s.hasCommonSeal;
  if (Array.isArray(s.transfers) && s.transfers.length) {
    transfers.value = s.transfers.map((t: any) => normalizeRestoredTransfer(t));
    // 레거시(단건 Section 105) 마이그레이션 — 저장본에 평면 Section 105 필드가 있고
    //   첫 transfer 의 section105 가 비어 있으면 첫 행으로 승격.
    migrateLegacySection105(s, transfers.value[0]);
  }
  executionDate.value = s.executionDate || '';
  resolutionDate.value = s.resolutionDate || '';
  transferDate.value = s.transferDate || '';
  letterDate.value = s.letterDate || '';
  if (Array.isArray(s.skippedFields)) skippedFields.value = new Set(s.skippedFields);
  if (Array.isArray(s.directors)) directors.value = s.directors;
  if (Array.isArray(s.waiverMembers)) waiverMembers.value = s.waiverMembers;
  registeredOfficeAddress.value = s.registeredOfficeAddress || registeredOfficeAddress.value;
  issueDate.value = s.issueDate || '';
  if (Array.isArray(s.shareCertSigners)) shareCertSigners.value = s.shareCertSigners;
  // 복원된 words 중 수동편집된 것 표시 (figures 자동생성값과 다르면) → 자동생성 덮어쓰기 방지.
  initConsiderationManualFlags();
  // 복원 완료 → 편집 dirty 판정용 기준 스냅샷 확정 (entity 로딩 override 반영 위해 nextTick)
  restored.value = true;
  nextTick(() => captureBaseline());
}

/** 복원된 transfer 행에 section105 / shareType 기본값 보장. */
function normalizeRestoredTransfer(t: any): TransferRow {
  const base = emptyTransferRow();
  const row: TransferRow = { ...base, ...t };
  row.shareType = t?.shareType || 'Ordinary';
  const s105 = { ...emptySection105(), ...(t?.section105 || {}) };
  if (!Array.isArray(s105.witnesses) || s105.witnesses.length === 0) {
    s105.witnesses = emptySection105().witnesses;
  }
  row.section105 = s105;
  return row;
}

/** 구버전 단건 Section 105 저장본 → 첫 transfer 행의 section105 로 승격. */
function migrateLegacySection105(s: Record<string, any>, firstRow?: TransferRow) {
  if (!firstRow) return;
  const legacyHas =
    s.considerationWords || s.considerationFigures || s.transferorDetails ||
    s.transferorId || s.transfereeDetails || s.transfereePerson || s.transfereeCorporate ||
    (Array.isArray(s.witnesses) && s.witnesses.some((w: any) => w?.name || w?.address || w?.occupation));
  const s105 = firstRow.section105 || (firstRow.section105 = emptySection105());
  const alreadyFilled =
    s105.considerationWords || s105.considerationFigures || s105.transferorId ||
    (s105.witnesses || []).some((w) => w?.name || w?.address || w?.occupation);
  if (!legacyHas || alreadyFilled) return;
  s105.considerationWords = s.considerationWords || '';
  s105.considerationFigures = s.considerationFigures || '';
  s105.transferorDetails = s.transferorDetails || '';
  s105.transferorId = s.transferorId || '';
  s105.transfereeDetails = s.transfereeDetails || '';
  s105.transfereePerson = s.transfereePerson || '';
  s105.transfereeCorporate = s.transfereeCorporate || '';
  if (s.transfereeKind) s105.transfereeKind = s.transfereeKind;
  if (s.transfereeRace) s105.transfereeRace = s.transfereeRace;
  if (s.transfereeCorporateType) s105.transfereeCorporateType = s.transfereeCorporateType;
  if (Array.isArray(s.witnesses) && s.witnesses.length) s105.witnesses = s.witnesses;
}
defineExpose({ restoreState, gatherState, createTask: () => handleCreateTask() });

// ─── Create / Update Task ───────────────────────────────────────────────────
onMounted(async () => {
  document.addEventListener('dragstart', _onGlobalDragStart);
  document.addEventListener('dragend', _onGlobalDragEnd);
  const taskCode = (props.selection.task as any)?.TASK_CODE;
  if (taskCode) {
    try {
      const cfg = await getTaskConfigByCode(taskCode);
      taskConfigSteps.value = (cfg as any)?.processSteps || [];
    } catch { /* ignore */ }
  }
});
onUnmounted(() => {
  document.removeEventListener('dragstart', _onGlobalDragStart);
  document.removeEventListener('dragend', _onGlobalDragEnd);
});

/** 공통(회사/거버닝/날짜/transfers 반복부) 변수 — DCR·Waiver 는 이것만, Section 105 는 여기에 행별 변수 merge. */
function buildBaseVariables(): Record<string, any> {
  return buildTransferSharesVariables({
    companyName: companyName.value,
    companyRegistrationNo: companyRegistrationNo.value,
    oldRegistrationNo: companyRegistrationNoOld.value,
    constitutionClauseNo: constitutionClauseNo.value,
    constitutionClauseNumber: constitutionClauseNo.value,
    governingDocumentType: governingDocumentType.value,
    directorCount: directorCount.value,
    memberCount: memberCount.value,
    hasCommonSeal: hasCommonSeal.value,
    transfers: transfers.value,
    totalSharesTransferred: totalSharesTransferred.value,
    executionDate: executionEffective.value,
    resolutionDate: resolutionEffective.value,
    transferDate: transferEffective.value,
    letterDate: letterEffective.value,
    directors: directors.value,
    waiverMembers: waiverMembers.value,
  });
}

/** 유효 transfer 행 (양도인/양수인 하나라도 있는 행) — Section 105 문서 1:1 대응. */
function validTransfers(): TransferRow[] {
  return transfers.value.filter((t) => String(t.transferorName || '').trim() || String(t.transfereeName || '').trim());
}

const SECTION105_RE = /section\s*105|form\s*of\s*transfer|transfer\s*of\s*securities/i;
const WAIVER_RE = /waiver|pre-?emption/i;
const DCR_RE = /director.*circular|circular.*resolution|\bdcr\b/i;
const SHARE_CERT_RE = /share\s*certificate|certificate\s*of\s*shares/i;

function isSection105Template(tName: string): boolean { return SECTION105_RE.test(String(tName || '')); }
function isShareCertTemplate(tName: string): boolean { return SHARE_CERT_RE.test(String(tName || '')); }

/** per-transfer/transferee 문서 라벨 — 템플릿명의 .docx 제거 + 양수인 이름 붙이고 .docx 한 번만.
 *   (기존 `${templateName} (Transfer 1)` 은 중간 .docx + 표시단계 .docx 재부착으로 "…docx…docx" 이중이 됨) */
function docLabelFor(templateName: string, who: string, idx: number, total: number): string {
  const base = String(templateName || '').replace(/\.docx?$/i, '').trim();      // "Section105 (Transfer)"
  const name = String(who || '').trim();
  let label = name ? `${base} — ${name}` : (total > 1 ? `${base} — Transfer ${idx + 1}` : base);
  label = label.replace(/\.+$/, '');                                            // 이름 끝 마침표 제거(BHD. → BHD)
  return `${label}.docx`;                                                       // 정확히 .docx 한 번
}

/** Share Certificate (transferee 1명당 1부) 변수 — 양도 표(New Cert / New Shares / Transferee)에서 파생. */
function buildShareCertVars(row: TransferRow): Record<string, any> {
  const shares = String(row.newShares || row.shares || '');
  return {
    cert_no: String(row.newCertificateNo || ''),
    no_of_shares: shares,
    shares_numeral: shares,
    shares_words: sharesToWords(shares),
    share_class: String(row.shareType || 'Ordinary'),
    member_name: String(row.transfereeName || ''),
    issue_date: issueEffective.value,
    registered_office_address: registeredOfficeAddress.value,
  };
}
/** Share Certificate 서명자 = 사용자가 선택한 Director / Secretary. */
function shareCertSignersOut(): Array<{ name: string; email: string; role: string }> | undefined {
  const s = shareCertSigners.value
    .filter((x) => x.name?.trim())
    .map((x) => ({ name: x.name!.trim(), email: String(x.email || '').trim(), role: 'signer' }));
  return s.length ? s : undefined;
}

/** 현재 양도인(매도자)들의 contactCd / 이름 집합 — Waiver 서명자에서 제외하는 기준. */
function transferorExclusion(): { cds: Set<string>; names: Set<string> } {
  const cds = new Set<string>(); const names = new Set<string>();
  for (const t of transfers.value) {
    const cc = String(t.transferorContactCd || '').trim();
    const nm = String(t.transferorName || '').trim().toUpperCase();
    if (cc) cds.add(cc);
    if (nm) names.add(nm);
  }
  return { cds, names };
}
/** Waiver 서명자 = 사용자 확정 목록(waiverMembers) − 매도자(양도인). 늦게 지정된 양도인도 표시에서 제외. */
function waiverSignerMembers(): SignerInput[] {
  const { cds, names } = transferorExclusion();
  return waiverMembers.value.filter((m) => {
    const cc = String(m.contactCd || '').trim();
    const nm = String(m.name || '').trim().toUpperCase();
    if (cc) return !cds.has(cc);
    return !(nm && names.has(nm));
  });
}

/** Waiver 서명자 자동 입력 — 기존 주주 풀(entityMembers) − 매도자(양도인) 으로 waiverMembers 채움. */
function autofillWaiverSigners() {
  const { cds, names } = transferorExclusion();
  waiverMembers.value = entityMembers.value
    .filter((m) => {
      const cc = String(m.contactCd || '').trim();
      const nm = String(m.name || '').trim().toUpperCase();
      if (cc) return !cds.has(cc);
      return !(nm && names.has(nm));
    })
    .map((m) => ({ ...m }));
}

/** DCR / Waiver 문서용 사이너 (Section 105 는 행별로 별도 계산). */
function pickSigners(tName: string): Array<{ name: string; email: string; role: string }> | undefined {
  const n = String(tName || '').toLowerCase();
  const dirSigners = directors.value.filter((d) => d.name?.trim())
    .map((d) => ({ name: d.name!.trim(), email: String(d.email || '').trim(), role: 'signer' }));
  const memSigners = waiverSignerMembers().filter((m) => m.name?.trim())
    .map((m) => ({ name: m.name!.trim(), email: String(m.email || '').trim(), role: 'signer' }));
  if (WAIVER_RE.test(n)) return memSigners.length ? memSigners : undefined;
  if (DCR_RE.test(n)) return dirSigners.length ? dirSigners : undefined;
  return undefined;
}

/** Section 105 (행별) 사이너 = 해당 transfer 의 양도인 + 양수인. */
function section105Signers(row: TransferRow): Array<{ name: string; email: string; role: string }> | undefined {
  const s: Array<{ name: string; email: string; role: string }> = [];
  if (String(row.transferorName || '').trim()) s.push({ name: String(row.transferorName).trim(), email: '', role: 'signer' });
  if (String(row.transfereeName || '').trim()) s.push({ name: String(row.transfereeName).trim(), email: '', role: 'signer' });
  return s.length ? s : undefined;
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!props.taskTitle?.trim()) { progress.value = 'Error: Task Title is required — enter it in the chat.'; return; }
  if (!entityCd.value) { progress.value = 'Error: Please select an entity in the chat first.'; return; }
  if (!isFormValid.value) { progress.value = 'Error: Please enter at least one transfer (transferor & transferee).'; return; }
  const taskTypeCd = (props.selection.task as any)?.TASK_CODE;
  if (!taskTypeCd) { progress.value = 'Error: Task Code not configured in PreSet.'; return; }

  creating.value = true;
  progress.value = 'Creating task...';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };

    const officerData = {
      entity: entityCd.value || '',
      entityName: companyName.value || '',
      entityRegNo: companyRegistrationNo.value || '',
      transactionDate: transferEffective.value || executionEffective.value || '',
      presetForm: gatherState(),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) { progress.value = 'Error: invalid edit task id.'; creating.value = false; return; }
      progress.value = 'Updating task...';
      try {
        await fetch(`/api/startTask/tasks/${taskId}/data`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskData: { formData: officerData } }),
        });
      } catch (err) {
        progress.value = `Error: ${err instanceof Error ? err.message : 'Task update failed'}`;
        creating.value = false; return;
      }
      try {
        await fetch(`/api/startTask/tasks/${taskId}/title`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskTitle: props.taskTitle }),
        });
      } catch { /* title optional */ }
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd,
          taskTypeName: (props.selection.task as any)?.TASK_NAME,
          taskTitle: props.taskTitle,
          taskFlow,
          officerData,
        }),
      });
      const taskJson = await taskRes.json();
      taskId = taskJson?.data?.taskId || taskJson?.taskId || null;
      if (!taskId) {
        progress.value = `Error: ${taskJson?.error?.message || taskJson?.message || 'Task creation failed'}`;
        creating.value = false; return;
      }
      progress.value = `Task created (ID: ${taskId}). Building documents...`;
    }

    const baseVariables = buildBaseVariables();
    const transferDateEff = transferEffective.value || '';
    const rows = validTransfers();

    // Section 105 는 Transfer 1건당 1부로 확장, 나머지(DCR/Waiver)는 단일 문서.
    const docsToSave: any[] = [];
    for (const d of (props.selection.documents || []) as any[]) {
      if (!d?.tmpId) continue;
      if (isSection105Template(d.templateName)) {
        const targets = rows.length ? rows : transfers.value.slice(0, 1);
        targets.forEach((row, idx) => {
          docsToSave.push({
            templateId: d.tmpId,
            // 양도 1건 = Section 105 1부 → 라벨에 양수인 이름(중복 .docx 방지).
            label: docLabelFor(d.templateName, row.transfereeName, idx, targets.length),
            personName: row.transfereeName || companyName.value || '',
            formData: { ...baseVariables, ...buildSection105Vars(row, { transferDate: transferDateEff }) },
            entityCd: entityCd.value || undefined,
            signers: section105Signers(row) || undefined,
          });
        });
      } else if (isShareCertTemplate(d.templateName)) {
        // Share Certificate — transferee(양수인) 1명당 1부.
        const targets = rows.length ? rows : transfers.value.slice(0, 1);
        targets.forEach((row, idx) => {
          docsToSave.push({
            templateId: d.tmpId,
            label: docLabelFor(d.templateName, row.transfereeName, idx, targets.length),
            personName: row.transfereeName || companyName.value || '',
            formData: { ...baseVariables, ...buildShareCertVars(row) },
            entityCd: entityCd.value || undefined,
            signers: shareCertSignersOut() || undefined,
          });
        });
      } else {
        docsToSave.push({
          templateId: d.tmpId,
          label: d.templateName,
          personName: companyName.value || '',
          formData: { ...baseVariables },
          entityCd: entityCd.value || undefined,
          signers: pickSigners(d.templateName) || undefined,
        });
      }
    }

    if (docsToSave.length > 0) {
      progress.value = `Saving ${docsToSave.length} document(s)...`;
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ taskId, documents: docsToSave, replaceExisting: editMode.value }),
        });
        const saveJson = await saveRes.json();
        if (!saveJson?.success) progress.value = `Documents save warning: ${saveJson?.message || 'unknown'}`;
      } catch (err) { console.warn('[TransferShares] Document save failed:', err); }
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      });
    } catch { /* ignore */ }

    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ presetTaskId: (props.selection.task as any)?.PRESET_TASK_ID, taskId }),
      });
    } catch { /* ignore */ }

    try {
      progress.value = 'Preparing next screen...';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' }).then((r) => r.json()).catch(() => null),
      ]);
      sessionStorage.setItem(
        `presetTaskDetailPrefetch:${taskId}`,
        JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }),
      );
    } catch { /* ignore */ }

    emit('task-created', { taskId, taskTitle: props.taskTitle });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (err: any) {
    console.error('[TransferShares] create/update task failed:', err);
    progress.value = `Error: ${err?.message || 'Failed to save task.'}`;
  } finally {
    creating.value = false;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
 * 문서-뷰(Studio) 브리지 — 기존 transfers[]/refs 모델 ↔ TRANSFER_DOC_CTX
 *   문서 컴포넌트(TransferDocVarField/parts)는 field-key 로 값을 읽고 쓰며, 여기서 실제 모델에 매핑한다.
 *   덕분에 createTask/gatherState/register 계약(transfers[] + section105)은 그대로 유지된다.
 * ═══════════════════════════════════════════════════════════════════════════ */
const activeKey = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);
const invalidKeys = ref<Set<string>>(new Set());
const packageDocRef = ref<{ focusField?: (k: string, s: number) => void } | null>(null);

function row0(): TransferRow {
  if (!transfers.value[0]) transfers.value[0] = emptyTransferRow();
  return transfers.value[0];
}
const ROW_COL: Record<string, keyof TransferRow> = {
  transferor_name: 'transferorName', certificate_no: 'certificateNo', shares: 'shares',
  transferee_name: 'transfereeName', new_certificate_no: 'newCertificateNo', new_shares: 'newShares',
};

function ctxValueOf(key: string): string {
  switch (key) {
    case 'company_name': return companyName.value;
    case 'company_registration_no': return companyRegistrationNo.value;
    case 'old_registration_no': return companyRegistrationNoOld.value;
    case 'constitution_clause_number': return constitutionClauseNo.value;
    case 'resolution_date': return resolutionDate.value;
    case 'transfer_date': return transferDate.value;
    case 'letter_date': return letterDate.value;
    case 'total_shares_transferred': return String(totalSharesTransferred.value || '');
    case 'number_of_units': return String(row0().shares ?? '');
    case 'securities_description': return securitiesDescriptionFromShareType(row0().shareType);
    case 'certificate_no': return String(row0().certificateNo ?? '');
    case 'transferor_name': return String(row0().transferorName ?? '');
    case 'transferee_name': return String(row0().transfereeName ?? '');
    case 'transferor_details': return String(row0().section105?.transferorDetails ?? '');
    case 'transferor_id': return String(row0().section105?.transferorId ?? '');
    case 'transferee_details': return String(row0().section105?.transfereeDetails ?? '');
    case 'transferee_person': return String(row0().section105?.transfereePerson ?? '');
    case 'transferee_corporate': return String(row0().section105?.transfereeCorporate ?? '');
    case 'consideration_words': return String(row0().section105?.considerationWords ?? '');
    case 'consideration_figures': return String(row0().section105?.considerationFigures ?? '');
    // Share Certificate (양도 표에서 자동 매핑)
    case 'cert_no': return String(row0().newCertificateNo ?? '');
    case 'no_of_shares': case 'shares_numeral': return String(row0().newShares ?? '');
    case 'shares_words': return sharesToWords(row0().newShares);
    case 'share_class': return String(row0().shareType ?? '');
    case 'member_name': return String(row0().transfereeName ?? '');
    case 'registered_office_address': return registeredOfficeAddress.value;
    case 'issue_date': return issueEffective.value;
  }
  // Section 105 — 양도 index(1-based)별 폼: s105_{n}_<field>
  const s105 = key.match(/^s105_(\d+)_(.+)$/);
  if (s105) {
    const i = Number(s105[1]) - 1; const sub = s105[2];
    const r = transfers.value[i]; if (!r) return '';
    const s = r.section105 || ({} as any);
    switch (sub) {
      case 'number_of_units': return String(r.shares ?? '');
      case 'securities_description': return securitiesDescriptionFromShareType(r.shareType);
      case 'certificate_no': return String(r.certificateNo ?? '');
      case 'transferor_name': return String(r.transferorName ?? '');
      case 'transferee_name': return String(r.transfereeName ?? '');
      case 'transferor_details': return String(s.transferorDetails ?? '');
      case 'transferor_id': return String(s.transferorId ?? '');
      case 'transferee_details': return String(s.transfereeDetails ?? '');
      case 'transferee_person': return String(s.transfereePerson ?? '');
      case 'transferee_corporate': return String(s.transfereeCorporate ?? '');
      case 'consideration_words': return String(s.considerationWords ?? '');
      case 'consideration_figures': return String(s.considerationFigures ?? '');
    }
    const w = sub.match(/^witness_(\d)_(name|address|occupation)$/);
    if (w) return String((ensureRowSection105(i).witnesses?.[Number(w[1]) - 1] as any)?.[w[2]] ?? '');
    return '';
  }
  // Share Certificate — 양수인 index(1-based)별 주권: sc_{n}_<field>
  const sc = key.match(/^sc_(\d+)_(.+)$/);
  if (sc) {
    const i = Number(sc[1]) - 1; const sub = sc[2];
    const r = transfers.value[i]; if (!r) return '';
    switch (sub) {
      case 'cert_no': return String(r.newCertificateNo ?? '');
      case 'no_of_shares': case 'shares_numeral': return String(r.newShares ?? '');
      case 'shares_words': return sharesToWords(r.newShares);
      case 'member_name': return String(r.transfereeName ?? '');
      case 'share_class': return String(r.shareType ?? '');
    }
    return '';
  }
  const wm = key.match(/^witness_(\d)_(name|address|occupation)$/);
  if (wm) { const w = ensureRowSection105(0).witnesses?.[Number(wm[1]) - 1] as any; return String(w?.[wm[2]] ?? ''); }
  const tk = parseTransferKey(key);
  if (tk) { const r = transfers.value[tk.index]; const f = ROW_COL[tk.col]; return r && f ? String((r as any)[f] ?? '') : ''; }
  return '';
}

function clearInvalid(key: string) {
  if (invalidKeys.value.has(key)) { invalidKeys.value.delete(key); invalidKeys.value = new Set(invalidKeys.value); }
}
function ctxSetField(key: string, val: string) {
  // Section 105 — 양도 index(1-based)별 폼
  const s105 = key.match(/^s105_(\d+)_(.+)$/);
  if (s105) {
    const i = Number(s105[1]) - 1; const sub = s105[2];
    const r = transfers.value[i]; if (!r) return;
    switch (sub) {
      case 'certificate_no': r.certificateNo = val; break;
      case 'transferor_name': r.transferorName = val; break;
      case 'transferee_name': r.transfereeName = val; break;
      case 'transferor_details': ensureRowSection105(i).transferorDetails = val; break;
      case 'transferor_id': ensureRowSection105(i).transferorId = val; break;
      case 'transferee_details': ensureRowSection105(i).transfereeDetails = val; break;
      case 'transferee_person': ensureRowSection105(i).transfereePerson = val; break;
      case 'transferee_corporate': ensureRowSection105(i).transfereeCorporate = val; break;
      case 'consideration_words': onConsiderationWordsInput(i, val); break;
      case 'consideration_figures': onConsiderationFiguresInput(i, val); break;
      case 'number_of_units': case 'securities_description': break; // derived
      default: {
        const w = sub.match(/^witness_(\d)_(name|address|occupation)$/);
        if (w) { const wit = ensureRowSection105(i).witnesses?.[Number(w[1]) - 1] as any; if (wit) wit[w[2]] = val; }
      }
    }
    if (val) clearInvalid(key);
    return;
  }
  // Share Certificate — 양수인 index(1-based)별 주권
  const sc = key.match(/^sc_(\d+)_(.+)$/);
  if (sc) {
    const i = Number(sc[1]) - 1; const sub = sc[2];
    const r = transfers.value[i]; if (!r) return;
    if (sub === 'share_class') { r.shareType = val; if (val) clearInvalid(key); }
    // cert_no/no_of_shares/shares_numeral/shares_words/member_name — 파생(양도 표에서 자동)
    return;
  }
  switch (key) {
    case 'company_name': companyName.value = val; break;
    case 'company_registration_no': companyRegistrationNo.value = val; break;
    case 'old_registration_no': companyRegistrationNoOld.value = val; break;
    case 'constitution_clause_number': constitutionClauseModel.value = val; break;
    case 'resolution_date': resolutionDate.value = val; break;
    case 'transfer_date': transferDate.value = val; break;
    case 'letter_date': letterDate.value = val; break;
    case 'certificate_no': row0().certificateNo = val; break;
    case 'transferor_name': row0().transferorName = val; break;
    case 'transferee_name': row0().transfereeName = val; break;
    case 'transferor_details': ensureRowSection105(0).transferorDetails = val; break;
    case 'transferor_id': ensureRowSection105(0).transferorId = val; break;
    case 'transferee_details': ensureRowSection105(0).transfereeDetails = val; break;
    case 'transferee_person': ensureRowSection105(0).transfereePerson = val; break;
    case 'transferee_corporate': ensureRowSection105(0).transfereeCorporate = val; break;
    case 'consideration_words': onConsiderationWordsInput(0, val); break;
    case 'consideration_figures': onConsiderationFiguresInput(0, val); break;
    case 'share_class': row0().shareType = val; break;
    case 'registered_office_address': registeredOfficeAddress.value = val; break;
    case 'issue_date': issueDate.value = val; break;
    case 'number_of_units': case 'securities_description': case 'total_shares_transferred': return; // derived
    case 'cert_no': case 'no_of_shares': case 'shares_numeral': case 'shares_words': case 'member_name': return; // Share Cert 파생
    default: {
      const wm = key.match(/^witness_(\d)_(name|address|occupation)$/);
      if (wm) { const w = ensureRowSection105(0).witnesses?.[Number(wm[1]) - 1] as any; if (w) w[wm[2]] = val; break; }
      const tk = parseTransferKey(key);
      if (tk) {
        const r = transfers.value[tk.index]; const f = ROW_COL[tk.col];
        if (r && f) (r as any)[f] = val;
        // 양도 정보(양도인/증서/주식수)를 직접 입력해도 New Cert No(마지막 cert + 1) 자동 — 비어있을 때만.
        if (r && (tk.col === 'transferor_name' || tk.col === 'certificate_no' || tk.col === 'shares') && !String(r.newCertificateNo || '').trim()) {
          r.newCertificateNo = resolveNextNewCertificateNo(tk.index);
        }
        // No. of Shares 를 입력하면 New No. of Shares 기본값도 동일하게(비어있을 때만) → Total 합계에 반영.
        if (r && tk.col === 'shares' && val && !String(r.newShares || '').trim()) r.newShares = val;
      }
    }
  }
  if (val) clearInvalid(key);
}

function ctxActivate(key: string) {
  activeKey.value = key;
  if (key === 'constitution_clause_number') {
    if (governingDocLocked.value) { activeKey.value = ''; return; }
    emit('request-person-lookup', {
      fieldGroup: 'constitution-clause', key: 'constitution_clause_number',
      clauseOptions: filteredClauseOptions.value, defaultBasis: governingDocumentType.value,
      defaultValue: constitutionClauseNo.value,
    });
    inputKey.value = ''; return;
  }
  const t = trfTypeOf(key);
  if (t === 'person') {
    const sm = key.match(/^s105_(\d+)_(.+)$/);
    const tk = parseTransferKey(key);
    if (sm) {
      const i = Number(sm[1]) - 1; const sub = sm[2];
      if (/^transferor_(name|details)$/.test(sub)) emit('request-person-lookup', { fieldGroup: 'rowTransferor', index: i, defaultRole: null });
      else if (/^transferee_(name|details)$/.test(sub)) emit('request-person-lookup', { fieldGroup: 'rowTransferee', index: i, defaultRole: null });
      else { const w = sub.match(/^witness_(\d)_name$/); if (w) emit('request-person-lookup', { fieldGroup: 'witness', index: i, subIndex: Number(w[1]) - 1, defaultRole: null }); }
    } else if (tk && tk.col === 'transferor_name') emit('request-person-lookup', { fieldGroup: 'rowTransferor', index: tk.index, defaultRole: null });
    else if (tk && tk.col === 'transferee_name') emit('request-person-lookup', { fieldGroup: 'rowTransferee', index: tk.index, defaultRole: null });
    else { const wm = key.match(/^witness_(\d)_name$/); if (wm) emit('request-person-lookup', { fieldGroup: 'witness', index: 0, subIndex: Number(wm[1]) - 1, defaultRole: null }); }
    inputKey.value = ''; return;
  }
  inputKey.value = key;
  inputText.value = ctxValueOf(key);
  if (t === 'date') dateMenu.value = true;
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false; commitInput();
}
function commitInput() { if (inputKey.value) ctxSetField(inputKey.value, inputText.value.trim()); inputKey.value = ''; activeKey.value = ''; }
function skipInput() { inputKey.value = ''; activeKey.value = ''; }

// ── flags / choices ──
function ctxFlag(name: string): boolean {
  if (name === 'is_multiple_director_company') return directorCount.value === 'multiple';
  if (name === 'is_multiple_member') return memberCount.value === 'multiple';
  if (name === 'has_common_seal') return hasCommonSeal.value;
  return false;
}
function ctxToggleFlag(name: string) {
  if (name === 'is_multiple_director_company') directorCount.value = directorCount.value === 'multiple' ? 'single' : 'multiple';
  else if (name === 'is_multiple_member') memberCount.value = memberCount.value === 'multiple' ? 'single' : 'multiple';
  else if (name === 'has_common_seal') hasCommonSeal.value = !hasCommonSeal.value;
}
function ctxChoiceOf(name: string): string { return name === 'resolution_basis' ? governingDocumentType.value : ''; }
function ctxSetChoice(name: string, val: string) {
  if (name === 'resolution_basis' && !governingDocLocked.value) {
    governingDocumentType.value = val as GoverningDocumentType;
    if (val === 'third_schedule') constitutionClauseNo.value = '';
  }
}

// ── transfers 반복부 ──
const transferCountRef = computed(() => transfers.value.length);
function ctxRemoveTransfer(index: number) { removeTransfer(index - 1); }
function ctxCertOptions(index: number) { return transferorCertOptions(index - 1); }
function ctxSelectCert(index: number, certNo: string) { onTransferorCertSelect(index - 1, certNo); }

// ── Section 105 체크박스 그룹 (양도 index 1-based) ──
function ctxTransfereeKindOf(index: number): 'individual' | 'corporate' | '' {
  return (transfers.value[index - 1]?.section105?.transfereeKind || '') as 'individual' | 'corporate' | '';
}
function ctxSetTransfereeKind(index: number, k: 'individual' | 'corporate') { onTransfereeKindChange(index - 1, k); }
function ctxRaceOf(index: number): string { return String(transfers.value[index - 1]?.section105?.transfereeRace || ''); }
function ctxSetRace(index: number, v: string) {
  const s = ensureRowSection105(index - 1);
  s.transfereeRace = (s.transfereeRace === v ? '' : v) as Section105Data['transfereeRace'];
  // Person/Body 두 칼럼이 동시에 보이므로, Race 체크 시 개인으로 자동 판정 + 법인유형 해제.
  if (s.transfereeRace) { s.transfereeKind = 'individual'; s.transfereeCorporateType = ''; }
}
function ctxBodyTypeOf(index: number): string { return String(transfers.value[index - 1]?.section105?.transfereeCorporateType || ''); }
function ctxSetBodyType(index: number, v: string) {
  const s = ensureRowSection105(index - 1);
  s.transfereeCorporateType = (s.transfereeCorporateType === v ? '' : v) as Section105Data['transfereeCorporateType'];
  if (s.transfereeCorporateType) { s.transfereeKind = 'corporate'; s.transfereeRace = ''; }
}

// ── [[Signers]] ← → directors / waiverMembers ──
function toEntry(s: SignerInput): SignerEntry { return { name: s.name, email: s.email || '', contactCd: s.contactCd || '' } as SignerEntry; }
function fromEntry(e: any): SignerInput { return { name: e?.name || '', email: e?.email || '', contactCd: e?.contactCd || '', nricNo: e?.nricNo || e?.identificationNo || '' }; }
function ctxSignersOf(varKey: string): SignerEntry[] {
  if (varKey === 'dcr') return directors.value.map(toEntry);
  if (varKey === 'waiver') return waiverSignerMembers().map(toEntry); // 매도자(양도인) 자동 제외
  if (varKey === 'share_cert') return shareCertSigners.value.map(toEntry);
  return [];
}
function ctxSetSigners(varKey: string, list: SignerEntry[]) {
  if (varKey === 'dcr') directors.value = (list || []).map(fromEntry);
  else if (varKey === 'waiver') waiverMembers.value = (list || []).map(fromEntry);
  else if (varKey === 'share_cert') shareCertSigners.value = (list || []).map(fromEntry);
}
function ctxCanAutofillSigners(varKey: string): boolean {
  if (varKey === 'waiver') return entityMembers.value.length > 0;
  return false;
}
function ctxAutofillSigners(varKey: string) {
  if (varKey === 'waiver') autofillWaiverSigners();
}

// ── 문서 종류 활성(연결된 템플릿) — 스튜디오가 연결된 문서만 표시 (with/without SC 자동 분기) ──
//   M32201(with SC) = 4문서 전부 연결 → 4단계. M32204(without SC) = Waiver·Share Certificate 미연결 → 2단계.
const enabledDocKinds = computed<Set<string>>(() => {
  const docs = (props.selection?.documents || []) as any[];
  const names = docs.map((d) => String(d?.templateName || d?.docCode || '')).filter(Boolean);
  const has = (re: RegExp) => names.some((n) => re.test(n));
  const set = new Set<string>();
  if (has(WAIVER_RE)) set.add('waiver');
  if (has(SECTION105_RE)) set.add('section105');
  if (has(DCR_RE)) set.add('dcr');
  if (has(SHARE_CERT_RE)) set.add('sharecert');
  // 매칭이 하나도 없으면(문서 미연결/이름 불일치) 전체 표시 — 안전 폴백(기존 동작 보존).
  if (set.size === 0) ['waiver', 'section105', 'dcr', 'sharecert'].forEach((k) => set.add(k));
  return set;
});
function ctxDocKindEnabled(kind: string): boolean { return enabledDocKinds.value.has(kind); }

function ctxIsInvalid(key: string): boolean { return invalidKeys.value.has(key); }

// studio 챗봇 정관 Clause 선택 결과 → governingDocumentType + constitutionClauseNo (등록 잠금이면 무시)
watch(() => props.appliedClause, (a) => {
  if (!a || governingDocLocked.value) return;
  const basis = a.governingBasis || 'constitution';
  governingDocumentType.value = basis as GoverningDocumentType;
  constitutionClauseNo.value = basis === 'third_schedule' ? '' : (a.clauseNumber || '');
});

provide(TRANSFER_DOC_CTX, {
  valueOf: ctxValueOf,
  labelOf: trfLabelize,
  typeOf: trfTypeOf,
  isDerived: isDerivedKey,
  activeKey,
  activate: ctxActivate,
  setFieldValue: ctxSetField,
  flag: ctxFlag,
  toggleFlag: ctxToggleFlag,
  choiceOf: ctxChoiceOf,
  setChoice: ctxSetChoice,
  clauseLocked: governingDocLocked as unknown as Ref<boolean>,
  transferCount: transferCountRef as unknown as Ref<number>,
  addTransfer,
  removeTransfer: ctxRemoveTransfer,
  certOptionsOf: ctxCertOptions,
  selectCert: ctxSelectCert,
  transfereeKindOf: ctxTransfereeKindOf,
  setTransfereeKind: ctxSetTransfereeKind,
  raceOf: ctxRaceOf,
  setRace: ctxSetRace,
  bodyTypeOf: ctxBodyTypeOf,
  setBodyType: ctxSetBodyType,
  entityCd: entityCd as unknown as Ref<string>,
  signersOf: ctxSignersOf,
  setSigners: ctxSetSigners,
  canAutofillSigners: ctxCanAutofillSigners,
  autofillSigners: ctxAutofillSigners,
  isInvalid: ctxIsInvalid,
  docKindEnabled: ctxDocKindEnabled,
});
</script>
<template>
  <div class="trf-panel" :class="{ 'trf-studio': studio }">
    <!-- 비-studio: 자체 헤더 -->
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ (selection.task as any)?.TASK_NAME || 'Transfer of Shares' }}</div>
        <div class="co-subtitle">Transfer of Shares</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')"><v-icon start size="16">mdi-arrow-left</v-icon>Back</v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <div class="co-readonly-box flex-grow-1" :class="{ 'co-readonly-empty': !taskTitle }">{{ taskTitle || 'Enter a task title in the chat' }}</div>
      <v-btn size="small" variant="tonal" color="primary" class="text-none" @click="emit('change-task-title')">Change</v-btn>
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ companyName || entityData?.companyName || 'Entity' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">Change</v-btn>
    </div>

    <!-- 활성 text/date 입력바 -->
    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">{{ trfLabelize(inputKey) }} <span v-if="trfTypeOf(inputKey) === 'date'" class="opt">(optional)</span></div>
      <div class="row">
        <v-menu v-if="trfTypeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field v-bind="mp" v-model="inputText" placeholder="DD/MM/YYYY" variant="outlined" density="compact" hide-details append-inner-icon="mdi-calendar" />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field v-else v-model="inputText" :placeholder="trfLabelize(inputKey)" variant="outlined" density="compact" hide-details autofocus @keydown.enter="commitInput" />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="trfTypeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">Skip</v-btn>
      </div>
    </div>

    <!-- 문서 패키지 (3단계) -->
    <div class="co-doc-body">
      <TransferSharesPackageDoc ref="packageDocRef" />
    </div>

    <!-- Footer (비-studio 또는 오류 시) -->
    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: progress.startsWith('Error') || progress.includes('warning') }">{{ progress }}</span>
        <v-btn v-if="!studio" color="primary" class="text-none" :loading="creating" :disabled="creating" :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'" @click="handleCreateTask">{{ editMode ? 'Update Task' : 'Create Task' }}</v-btn>
      </div>
    </div>
  </div>
</template>

<style scoped>
.trf-panel { display:flex; flex-direction:column; }
.trf-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.trf-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; } .co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-readonly-box { font-size:13px; color:#33405a; padding:6px 10px; background:#f6f8fc; border-radius:8px; }
.co-readonly-box.co-readonly-empty { color:#9aa6bd; font-style:italic; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; position:sticky; top:0; z-index:5; }
.co-inputbar .lbl { font-size:12px; font-weight:700; color:#1B84FF; margin-bottom:6px; } .co-inputbar .opt { color:#8a94a6; font-weight:400; }
.co-inputbar .row { display:flex; align-items:center; gap:8px; }
.co-doc-body { padding:20px; background:#eceff5; }
.co-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-top:1px solid #eef0f6; }
.co-foot-right { display:flex; align-items:center; gap:12px; }
.co-progress { font-size:12px; color:#5b6472; max-width:420px; }
.co-progress.err { color:#d64545; font-weight:600; }
</style>
